import React, { useState, useRef } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import styled from 'styled-components'
import {
  generateFormFileUploadUrl,
  confirmFormFileUpload,
  deleteFormFile
} from 'services/taskService'
import db from 'services/offlineDb'
import { updateSyncCountsRoutine, isOnline as checkIsOnline } from 'ducks/offline/actions'
import { enqueueSyncAction, checkStorageQuota } from 'services/offlineSync'
import { toast } from 'react-toastify'
import ImageViewer from 'components/ImageViewer'
import SearchableSelect from 'components/SearchableSelect'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AuditQuestionField from './AuditQuestionField'
import FileList from './FileList'

const FIELD_TYPES = {
  text: 'text',
  textarea: 'textarea',
  number: 'number',
  date: 'date',
  select: 'select',
  multiselect: 'multiselect',
  checkbox: 'checkbox',
  radio: 'radio',
  yesNo: 'yes_no',
  file: 'file',
  image: 'image',
  video: 'video',
  // 'gps' is retired as a field type — location is now captured
  // automatically on every save/submit via the geolocation API and
  // attached to the form response (see FormRenderer). The case below
  // is kept so legacy schemas that still have a `gps` field render
  // without breaking, but the form builder no longer offers it.
  gps: 'gps',
  checklistUpload: 'checklist_upload',
  signature: 'signature',
  email: 'email',
  phone: 'phone'
}

const supportsWebP = (() => {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    return canvas.toDataURL('image/webp').startsWith('data:image/webp')
  } catch {
    return false
  }
})()

const compressImage = (file, maxWidth = 3840, quality = 0.92) => {
  return new Promise(resolve => {
    // Skip non-image files
    if (!file.type.startsWith('image/') || file.type === 'image/gif') {
      resolve(file)
      return
    }

    // Skip small files (under 2MB)
    if (file.size < 2 * 1024 * 1024) {
      resolve(file)
      return
    }

    const img = new Image()
    const canvas = document.createElement('canvas')
    const reader = new FileReader()

    reader.onload = e => {
      img.onload = () => {
        let { width, height } = img

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width)
          width = maxWidth
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, width, height)

        // PNG stays lossless, others use WebP (better quality/size) or JPEG
        const isPng = file.type === 'image/png'
        const outputType = isPng ? 'image/png' : (supportsWebP ? 'image/webp' : 'image/jpeg')
        const outputExt = isPng ? '.png' : (supportsWebP ? '.webp' : '.jpg')
        const outputName = file.name.replace(/\.[^.]+$/, outputExt)

        canvas.toBlob(
          blob => {
            if (!blob || blob.size >= file.size) {
              resolve(file)
              return
            }
            const compressed = new File([blob], outputName, {
              type: outputType,
              lastModified: file.lastModified
            })
            resolve(compressed)
          },
          outputType,
          isPng ? undefined : quality
        )
      }
      img.onerror = () => resolve(file)
      img.src = e.target.result
    }
    reader.onerror = () => resolve(file)
    reader.readAsDataURL(file)
  })
}

const isViewableImageUrl = url => !!url && /\.(jpe?g|png|gif|webp|bmp)$/i.test(url.split('?')[0])

const FileField = ({ field, value, onChange, readOnly, taskId }) => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState(null)
  const [thumbnailFailed, setThumbnailFailed] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  // Normalize file value to support both camelCase and snake_case keys
  const rawFileValue = value && typeof value === 'object' ? value : null
  const fileValue = rawFileValue ? {
    ...rawFileValue,
    fileId: rawFileValue.fileId || rawFileValue.file_id,
    fileName: rawFileValue.fileName || rawFileValue.file_name,
    url: rawFileValue.url,
    thumbnailUrl: rawFileValue.thumbnailUrl || rawFileValue.thumbnail_url,
    description: rawFileValue.description,
    _pending: rawFileValue._pending,
    _offlineFileId: rawFileValue._offlineFileId || rawFileValue._offline_file_id,
    _thumbnailUrl: rawFileValue._thumbnailUrl || rawFileValue._thumbnail_url,
    descriptionGenerating: rawFileValue.descriptionGenerating || rawFileValue.description_generating,
    size: rawFileValue.size || 0
  } : null

  const validateFile = (file, isPostCompression = false) => {
    if (field.acceptedMimeTypes && field.acceptedMimeTypes.length > 0) {
      let fileType = file.type
      // Normalize common MIME type variants
      if (fileType === 'image/jpg') fileType = 'image/jpeg'

      const accepted = field.acceptedMimeTypes.some(type => {
        // Handle bare type like "image" (without /*) — treat as "image/*"
        if (!type.includes('/') && fileType.startsWith(type + '/')) return true
        if (type.endsWith('/*')) {
          const prefix = type.replace('/*', '/')
          if (fileType.startsWith(prefix)) return true
          // If accepting image/*, also accept image/webp (post-compression format)
          if (type === 'image/*' && fileType === 'image/webp') return true
          return false
        }
        let normalizedAccepted = type
        if (normalizedAccepted === 'image/jpg') normalizedAccepted = 'image/jpeg'
        return fileType === normalizedAccepted
      })
      if (!accepted && !isPostCompression) {
        return `File type "${file.type}" is not accepted. Allowed: ${field.acceptedMimeTypes.join(', ')}`
      }
    }
    const maxSizeMb = field.maxFileSizeMb || 10
    if (file.size > maxSizeMb * 1024 * 1024) {
      return `File size exceeds ${maxSizeMb}MB limit.`
    }
    return null
  }

  const handleFileSelect = async e => {
    const rawFile = e.target.files?.[0]
    if (!rawFile || !taskId) return

    setError(null)
    setUploading(true)
    setUploadProgress(0)

    let file = rawFile
    try {
      file = await compressImage(rawFile)

      // Validate AFTER compression (file type may change to webp)
      const validationError = validateFile(file, file !== rawFile)
      if (validationError) {
        setError(validationError)
        setUploading(false)
        return
      }

      // OFFLINE: store compressed blob in IDB for later sync
      if (!checkIsOnline()) {
        const offlineFileId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
        let quotaInfo
        try {
          quotaInfo = await checkStorageQuota(file.size)
        } catch (quotaError) {
          toast.error(quotaError.message, { autoClose: 8000 })
          setError(quotaError.message)
          setUploading(false)
          return
        }

        if (quotaInfo?.percentUsed >= 80) {
          toast.warning(
            `Offline storage is ${quotaInfo.percentUsed}% full (${quotaInfo.usedMb}MB). ` +
            'Connect to the internet soon to sync and free up space.',
            { autoClose: 6000, toastId: 'storage-warning' }
          )
        }

        await db.pendingFiles.add({
          taskId,
          fieldKey: field.key,
          fileName: file.name || rawFile.name,
          contentType: file.type || 'image/jpeg',
          blob: file,
          offlineFileId,
          status: 'pending',
          createdAt: new Date().toISOString()
        })

        await enqueueSyncAction({
          type: 'UPLOAD_FILE',
          payload: { taskId, fieldKey: field.key, offlineFileId },
          offlineFileId
        })

        onChange({
          _offlineFileId: offlineFileId,
          fileName: file.name || rawFile.name,
          _pending: true,
          _thumbnailUrl: URL.createObjectURL(file)
        })

        toast.info('Photo saved offline. Will upload when connected.')
        setUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      const urlResponse = await generateFormFileUploadUrl({
        taskId,
        fieldKey: field.key,
        filename: file.name,
        contentType: file.type
      })
      const { fileId, uploadUrl } = urlResponse.data

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100))
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(`Upload failed with status ${xhr.status}`))
        }
        xhr.onerror = () => reject(new Error('Upload failed'))
        xhr.send(file)
      })

      const confirmResponse = await confirmFormFileUpload({
        taskId,
        fileId,
        fieldKey: field.key
      })

      const fileData = confirmResponse.data
      onChange({
        fileId: fileData.id,
        fileName: fileData.name,
        description: fileData.description || '',
        descriptionGenerating: fileData.descriptionGenerating || false,
        url: fileData.url,
        size: fileData.size || 0
      })
    } catch (err) {
      const isNetworkErr = !checkIsOnline()
        || err?.message === 'Network Error'
        || err?.code === 'ERR_NETWORK'
        || err?.message === 'Upload failed'
        || err?.message === 'Upload network error'

      if (isNetworkErr) {
        // Fallback: save offline for later sync
        try {
          const offlineFileId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
          await db.pendingFiles.add({
            taskId,
            fieldKey: field.key,
            fileName: file.name || rawFile.name,
            contentType: file.type || 'image/jpeg',
            blob: file,
            offlineFileId,
            status: 'pending',
            createdAt: new Date().toISOString()
          })
          await enqueueSyncAction({
            type: 'UPLOAD_FILE',
            payload: { taskId, fieldKey: field.key, offlineFileId },
            offlineFileId
          })
          onChange({
            _offlineFileId: offlineFileId,
            fileName: file.name || rawFile.name,
            _pending: true,
            _thumbnailUrl: URL.createObjectURL(file)
          })
          toast.info('Photo saved offline. Will upload when connected.')
          if (window.reduxStore) {
            window.reduxStore.dispatch(updateSyncCountsRoutine.trigger())
          }
        } catch (offlineErr) {
          setError('Failed to save photo offline: ' + (offlineErr.message || 'Unknown error'))
        }
      } else {
        setError(err.response?.data?.message || err.message || 'Upload failed')
      }
    } finally {
      setUploading(false)
      setUploadProgress(0)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleRemove = async () => {
    console.log('[FileField] handleRemove called', { taskId, fileValue, readOnly })
    if (!taskId) return

    // Offline pending file — just remove from IDB and clear value
    if (fileValue?._pending && fileValue?._offlineFileId) {
      try {
        await db.pendingFiles.where('offlineFileId').equals(fileValue._offlineFileId).delete()
        await db.syncQueue.where('status').equals('pending')
          .filter(e => e.type === 'UPLOAD_FILE' && e.payload?.offlineFileId === fileValue._offlineFileId)
          .delete()
        onChange(null)
        if (window.reduxStore) {
          window.reduxStore.dispatch(updateSyncCountsRoutine.trigger())
        }
      } catch (err) {
        setError('Failed to remove pending file')
      }
      return
    }

    if (!fileValue?.fileId) {
      console.warn('[FileField] handleRemove: no fileId, cannot delete', fileValue)
      // Still clear the value locally even if we can't delete from server
      onChange(null)
      return
    }

    if (!checkIsOnline()) {
      // Offline: enqueue deletion for later sync
      try {
        await enqueueSyncAction({
          type: 'DELETE_FORM_FILE',
          payload: { taskId, fileId: fileValue.fileId, fieldKey: field.key }
        })
        onChange(null)
        if (window.reduxStore) {
          window.reduxStore.dispatch(updateSyncCountsRoutine.trigger())
        }
        toast.info('File removal queued. Will sync when connected.')
      } catch (err) {
        setError('Failed to queue file removal')
      }
      return
    }

    try {
      await deleteFormFile({ taskId, fileId: fileValue.fileId })
      onChange(null)
    } catch (err) {
      // Network error fallback — queue offline
      if (!checkIsOnline() || err?.message === 'Network Error' || err?.code === 'ERR_NETWORK') {
        try {
          await enqueueSyncAction({
            type: 'DELETE_FORM_FILE',
            payload: { taskId, fileId: fileValue.fileId, fieldKey: field.key }
          })
          onChange(null)
          toast.info('File removal queued. Will sync when connected.')
        } catch (e) {
          setError('Failed to queue file removal')
        }
        return
      }
      setError(err.response?.data?.message || 'Failed to remove file')
    }
  }

  const handleDescriptionChange = newDescription => {
    if (fileValue) {
      onChange({ ...fileValue, description: newDescription })
    }
  }

  const isImageFile = fileName => {
    if (!fileName) return false
    const ext = fileName.toLowerCase().split('.').pop()
    return [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'webp',
      'bmp',
      'heic',
      'heif'
    ].includes(ext)
  }

  if (fileValue) {
    const isPending = fileValue._pending
    const viewableUrl = isViewableImageUrl(fileValue.url) ? fileValue.url : null
    const imageUrl = isPending
      ? fileValue._thumbnailUrl
      : (!thumbnailFailed && fileValue.thumbnailUrl) || viewableUrl
    const showThumbnail = !!imageUrl && isImageFile(fileValue.fileName)
    const viewerUrl = isPending ? fileValue._thumbnailUrl : viewableUrl || imageUrl
    const canView = showThumbnail && !!viewerUrl
    const showDescription = !field.generateDescription && !!fileValue.description

    return (
      <div>
        <FileDisplay $hasImage={showThumbnail}>
          {showThumbnail && (
            <div style={{ position: 'relative' }}>
              <FileThumbnail
                src={imageUrl}
                alt={fileValue.fileName}
                onError={() => {
                  if (!isPending && fileValue.thumbnailUrl && !thumbnailFailed) setThumbnailFailed(true)
                }}
                onClick={() => canView && setViewerOpen(true)}
                onKeyDown={e => {
                  if (canView && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    setViewerOpen(true)
                  }
                }}
                role='button'
                tabIndex={0}
                aria-label={`View the photo ${fileValue.fileName}`}
                style={{ cursor: 'pointer' }}
              />
              {isPending && (
                <PendingBadge>Pending upload</PendingBadge>
              )}
            </div>
          )}
          <FileInfo>
            <FileName>
              {canView ? (
                <FileNameButton type='button' onClick={() => setViewerOpen(true)}>
                  {fileValue.fileName}
                </FileNameButton>
              ) : fileValue.url ? (
                <FileLink
                  href={fileValue.url}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {fileValue.fileName}
                </FileLink>
              ) : (
                fileValue.fileName
              )}
            </FileName>
            {fileValue.size > 0 && (
              <FileSize>
                {fileValue.size < 1024
                  ? `${fileValue.size} B`
                  : fileValue.size < 1024 * 1024
                    ? `${(fileValue.size / 1024).toFixed(1)} KB`
                    : `${(fileValue.size / (1024 * 1024)).toFixed(1)} MB`}
              </FileSize>
            )}
            {showDescription && (
              <PhotoDescription>{fileValue.description}</PhotoDescription>
            )}
          </FileInfo>
          {!readOnly && (
            <FileActions>
              <ReplaceButton type='button' onClick={() => fileInputRef.current?.click()}>
                Replace
              </ReplaceButton>
              <RemoveButton type='button' onClick={handleRemove}>
                Remove
              </RemoveButton>
            </FileActions>
          )}
        </FileDisplay>
        {!readOnly && (
          <>
            <input
              ref={fileInputRef}
              type='file'
              onChange={handleFileSelect}
              accept={field.fileAcceptMode === 'photos_only' ? 'image/*' : (field.acceptedMimeTypes?.join(',') || undefined)}
              style={{ display: 'none' }}
            />
            <input
              ref={cameraInputRef}
              type='file'
              accept='image/*'
              capture='environment'
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </>
        )}

        {viewerOpen && (
          <ImageViewer
            open
            onClose={() => setViewerOpen(false)}
            file={{ id: fileValue.fileId, url: viewerUrl, name: fileValue.fileName, displayName: fileValue.fileName }}
          />
        )}

        {fileValue.descriptionGenerating && (
          <GeneratingText>Generating AI description...</GeneratingText>
        )}

        {(fileValue.description || !fileValue.descriptionGenerating) &&
          field.generateDescription && (
            <div>
              <DescriptionLabel>Description</DescriptionLabel>
              <DescriptionTextarea
                value={fileValue.description || ''}
                onChange={e => handleDescriptionChange(e.target.value)}
                readOnly={readOnly}
                placeholder='File description...'
                $readOnly={readOnly}
              />
            </div>
          )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    )
  }

  const handleCameraClick = () => {
    cameraInputRef.current?.click()
  }

  const handleFileClick = () => {
    fileInputRef.current?.click()
  }

  // Check if device likely has a camera (mobile or tablet)
  const isMobileDevice =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      typeof navigator !== 'undefined' ? navigator.userAgent : ''
    )

  return (
    <div>
      {!readOnly && (
        <>
          {uploading ? (
            <UploadProgressWrapper>
              <UploadProgressText>
                Uploading... {uploadProgress}%
              </UploadProgressText>
              <ProgressBarTrack>
                <ProgressBarFill style={{ width: `${uploadProgress}%` }} />
              </ProgressBarTrack>
            </UploadProgressWrapper>
          ) : (
            <FileUploadOptions>
              {/* Camera input - hidden, triggered by button */}
              <input
                ref={cameraInputRef}
                type='file'
                accept='image/*'
                capture='environment'
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {/* File input - hidden, triggered by button */}
              <input
                ref={fileInputRef}
                type='file'
                onChange={handleFileSelect}
                accept={field.fileAcceptMode === 'photos_only' ? 'image/*' : (field.acceptedMimeTypes?.join(',') || undefined)}
                style={{ display: 'none' }}
              />

              {field.fileAcceptMode !== 'files_only' && (
                <UploadButton type='button' onClick={handleCameraClick} $primary>
                  <CameraIcon />
                  Take Photo
                </UploadButton>
              )}

              <UploadButton type='button' onClick={handleFileClick}>
                <UploadIcon />
                {isMobileDevice ? 'Choose File' : 'Upload File'}
              </UploadButton>
            </FileUploadOptions>
          )}
        </>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  )
}

const normalizeChecklistUploadValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      completed: value.completed === true,
      completedAt: value.completedAt || null,
      lastOpenedAt: value.lastOpenedAt || null,
      lastRefreshedAt: value.lastRefreshedAt || null
    }
  }

  return {
    completed: value === true,
    completedAt: null,
    lastOpenedAt: null,
    lastRefreshedAt: null
  }
}

const ChecklistUploadField = ({
  value,
  onChange,
  readOnly,
  task,
  onTaskRefresh
}) => {
  const uploadWindowRef = useRef(null)
  const shouldRefreshOnReturnRef = useRef(false)
  const uploadSessionActiveRef = useRef(false)
  const refreshIntervalRef = useRef(null)
  const refreshTimeoutRef = useRef(null)
  const [refreshing, setRefreshing] = useState(false)
  const normalizedValue = normalizeChecklistUploadValue(value)
  const checklistFiles = task?.checklistFiles || task?.checklist_files || []
  const taskMetadata = task?.metadata || {}
  const sourceType = taskMetadata.source
  const isTaskFileUpload = ['analytics_checklist_item', 'task_file_upload'].includes(sourceType)
  const obligationLink = task?.complianceObligation || task?.compliance_obligation || null
  const isComplianceTask = !!obligationLink ||
    sourceType === 'compliance' ||
    !!(task?.metadata?.programmeObligationId || task?.metadata?.programme_obligation_id)
  const complianceBpId = obligationLink?.bpId || obligationLink?.bp_id || task?.building?.id || null
  const complianceUnitIds = (task?.units || []).map(u => u?.id).filter(Boolean)
  const complianceUnitId = complianceUnitIds.length > 0 ? null : (task?.unit?.id || null)
  const complianceFileTypeId = obligationLink?.fileTypeId || obligationLink?.file_type_id || null
  const complianceUploadQuery = [
    task?.id ? `taskId=${encodeURIComponent(task.id)}` : null,
    complianceUnitIds.length > 0 && complianceUnitIds.length <= 20
      ? `units=${complianceUnitIds.map(encodeURIComponent).join(',')}`
      : null,
    complianceUnitId ? `units=${encodeURIComponent(complianceUnitId)}` : null,
    complianceFileTypeId ? `type=${encodeURIComponent(complianceFileTypeId)}` : null,
  ].filter(Boolean).join('&')
  const complianceUploadUrl = isComplianceTask && complianceBpId
    ? `/files/upload/${complianceBpId}${complianceUploadQuery ? `?${complianceUploadQuery}` : ''}`
    : null
  const checklistRedirectUrl =
    taskMetadata.redirectUrl || taskMetadata.redirect_url || null
  const taskFileTypeIds = (
    taskMetadata.fileTypeIds ||
    taskMetadata.file_type_ids ||
    [taskMetadata.fileTypeId || taskMetadata.file_type_id]
  ).filter(Boolean).map(String)
  const requiredFileTypes = taskMetadata.fileTypes || taskMetadata.file_types || []
  const taskUploadBpId = task?.building?.id || task?.buildingPassport?.id || task?.building_passport?.id || null
  const taskUploadUnitIds = (task?.units || []).map(unit => unit?.id).filter(Boolean)
  const taskUploadUnitId = taskUploadUnitIds.length === 0 ? task?.unit?.id : null
  const taskUploadParams = new URLSearchParams({ method: 'traditional' })
  if (task?.id) taskUploadParams.append('taskId', task.id)
  if (taskFileTypeIds.length === 1) taskUploadParams.append('type', taskFileTypeIds[0])
  if (taskUploadUnitIds.length > 0) taskUploadParams.append('units', taskUploadUnitIds.join(','))
  if (taskUploadUnitId) taskUploadParams.append('unit', taskUploadUnitId)
  const taskUploadUrl = isTaskFileUpload && taskUploadBpId && task?.id
    ? `/files/upload/${taskUploadBpId}?${taskUploadParams.toString()}`
    : null
  const redirectUrl = sourceType === 'analytics_checklist_item'
    ? checklistRedirectUrl
    : (checklistRedirectUrl || taskUploadUrl || complianceUploadUrl)
  const isSupported = !!redirectUrl
  const uploadedFileTypeIds = new Set(
    checklistFiles
      .map(file => file?.fileType?.id || file?.fileTypeId || file?.file_type_id)
      .filter(Boolean)
      .map(String)
  )
  const hasAnyUploadedFiles = checklistFiles.length > 0
  const hasUploadedFiles = taskFileTypeIds.length > 0
    ? taskFileTypeIds.every(fileTypeId => uploadedFileTypeIds.has(fileTypeId))
    : hasAnyUploadedFiles
  const hasUploadedFilesRef = useRef(hasUploadedFiles)
  hasUploadedFilesRef.current = hasUploadedFiles

  const triggerRefresh = React.useCallback(async () => {
    if (!onTaskRefresh) return

    setRefreshing(true)
    try {
      await Promise.resolve(onTaskRefresh())
      onChange({
        ...normalizedValue,
        lastRefreshedAt: new Date().toISOString()
      })
    } finally {
      setRefreshing(false)
    }
  }, [normalizedValue, onChange, onTaskRefresh])

  React.useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      )
        return

      // Keep refreshing on every focus as long as the upload session is active
      // and files haven't been found yet. This handles the case where
      // window.open with noopener returns null and shouldRefreshOnReturnRef
      // was already consumed by a previous focus event.
      const shouldRefresh =
        shouldRefreshOnReturnRef.current ||
        uploadWindowRef.current ||
        (uploadSessionActiveRef.current && !hasUploadedFilesRef.current)

      if (!shouldRefresh) return

      // Only clear the one-shot flag; uploadSessionActiveRef stays true
      // until files are actually found
      shouldRefreshOnReturnRef.current = false
      triggerRefresh()

      if (uploadWindowRef.current?.closed) {
        uploadWindowRef.current = null
      }
    }

    window.addEventListener('focus', handleVisibilityOrFocus)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityOrFocus)
    }

    return () => {
      window.removeEventListener('focus', handleVisibilityOrFocus)
      if (typeof document !== 'undefined') {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityOrFocus
        )
      }
    }
  }, [triggerRefresh])

  React.useEffect(() => {
    if (!task?.id) return undefined

    const storageKey = `checklist-task-upload-complete-${task.id}`

    const handleUploadCompleted = event => {
      if (event.key !== storageKey || !event.newValue) return
      triggerRefresh()
    }

    window.addEventListener('storage', handleUploadCompleted)

    return () => {
      window.removeEventListener('storage', handleUploadCompleted)
    }
  }, [task?.id, triggerRefresh])

  React.useEffect(() => {
    if (hasUploadedFiles) {
      shouldRefreshOnReturnRef.current = false
      uploadSessionActiveRef.current = false

      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }
    }
  }, [hasUploadedFiles])

  React.useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [])

  const handleOpenUpload = () => {
    if (!redirectUrl) return
    if (!checkIsOnline()) {
      toast.warning('File upload is not available while offline.')
      return
    }

    shouldRefreshOnReturnRef.current = true
    uploadSessionActiveRef.current = true
    uploadWindowRef.current = window.open(
      redirectUrl,
      '_blank',
      'noopener,noreferrer'
    )

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
    }

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    // Poll briefly after opening the uploader so the task can pick up the
    // linked file as soon as upload/finalize completes in the other tab.
    refreshIntervalRef.current = setInterval(() => {
      triggerRefresh()
    }, 2500)

    refreshTimeoutRef.current = setTimeout(() => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
      refreshTimeoutRef.current = null
    }, 45000)

    onChange({
      ...normalizedValue,
      lastOpenedAt: new Date().toISOString()
    })
  }

  const handleCheckboxChange = checked => {
    onChange({
      ...normalizedValue,
      completed: checked,
      completedAt: checked ? new Date().toISOString() : null
    })
  }

  const handlePreviewFile = file => {
    const previewUrl = file?.url || file?.originalUrl || file?.original_url
    if (!previewUrl) return

    window.open(previewUrl, '_blank', 'noopener,noreferrer')
  }

  const handleDownloadFile = file => {
    const downloadUrl = file?.url || file?.originalUrl || file?.original_url
    if (!downloadUrl) return

    const anchor = document.createElement('a')
    anchor.href = downloadUrl
    anchor.target = '_blank'
    anchor.download =
      file?.displayName ||
      file?.display_name ||
      file?.name ||
      file?.versions?.[0]?.name ||
      'document'
    anchor.click()
  }

  if (readOnly) {
    return (
      <ChecklistUploadCard>
        {checklistFiles.length > 0 ? (
          <ChecklistFilesList>
            {checklistFiles.map(file => {
              const fileName =
                file.displayName ||
                file.display_name ||
                file.name ||
                file.versions?.[0]?.name
              const thumbnailUrl = file.thumbnailUrl || file.thumbnail_url

              return (
                <ChecklistReviewFileCard key={file.id}>
                  <ChecklistReviewPreviewButton
                    type='button'
                    onClick={() => handlePreviewFile(file)}
                  >
                    {thumbnailUrl ? (
                      <ChecklistReviewThumbnail
                        src={thumbnailUrl}
                        alt={fileName}
                      />
                    ) : (
                      <ChecklistReviewPlaceholder>
                        No preview
                      </ChecklistReviewPlaceholder>
                    )}
                  </ChecklistReviewPreviewButton>

                  <ChecklistReviewContent>
                    <ChecklistFileName>{fileName}</ChecklistFileName>
                    <ChecklistReviewActions>
                      <ChecklistSecondaryButton
                        type='button'
                        onClick={() => handlePreviewFile(file)}
                      >
                        Preview
                      </ChecklistSecondaryButton>
                      <ChecklistSecondaryButton
                        type='button'
                        onClick={() => handleDownloadFile(file)}
                      >
                        Download
                      </ChecklistSecondaryButton>
                    </ChecklistReviewActions>
                  </ChecklistReviewContent>
                </ChecklistReviewFileCard>
              )
            })}
          </ChecklistFilesList>
        ) : (
          <ChecklistUploadWarning>
            {isComplianceTask
              ? 'No evidence has been uploaded for this task yet.'
              : isTaskFileUpload
                ? 'No required files have been uploaded for this task yet.'
                : 'No uploaded file matched this checklist item yet.'}
          </ChecklistUploadWarning>
        )}
      </ChecklistUploadCard>
    )
  }

  return (
    <ChecklistUploadCard>
      <ChecklistUploadInfo>
        <ChecklistUploadTitle>
          {hasUploadedFiles
            ? (isComplianceTask
              ? 'Evidence uploaded to the Building Passport.'
              : isTaskFileUpload
                ? 'All required files are linked to this task.'
                : 'Uploaded file linked to this task.')
            : (isComplianceTask
              ? 'Upload the evidence to the Building Passport.'
              : isTaskFileUpload
                ? 'Upload the required files to the Building Passport.'
                : 'Upload this file from the Building Passport upload page.')}
        </ChecklistUploadTitle>
        <ChecklistUploadText>
          {hasUploadedFiles
            ? (isComplianceTask
              ? 'Your uploaded documents are shown below. They are automatically analysed and classified, and count as compliance evidence. Upload more if needed, then confirm below.'
              : isTaskFileUpload
                ? 'The required document types are now linked to this task. You can preview them below or upload replacements before sending the task for review.'
                : 'The uploaded document is now linked to this task. You can preview it here, download it, or re-upload a replacement before sending it for review.')
            : (isComplianceTask
              ? 'Use the button below to open the Building Passport upload page. Certificates and reports uploaded there are automatically analysed and classified. Then return here and confirm the evidence is uploaded.'
              : isTaskFileUpload
                ? `Use the button below to upload the required document type${taskFileTypeIds.length === 1 ? '' : 's'}. Uploaded files are linked back to this task automatically.`
                : 'The file is not uploaded inside the task form. Use the button below to open the correct upload page, then return here and confirm when the upload is ready for review. After that, submit the task below to send it for approval.')}
        </ChecklistUploadText>
        {isTaskFileUpload && requiredFileTypes.length > 0 && (
          <RequiredFileTypes>
            {requiredFileTypes.map(fileType => (
              <RequiredFileType key={fileType.id || fileType.name}>
                {fileType.name}
              </RequiredFileType>
            ))}
          </RequiredFileTypes>
        )}
        {!isSupported && (
          <ChecklistUploadWarning>
            This task does not currently have a checklist upload destination
            linked to it.
          </ChecklistUploadWarning>
        )}
      </ChecklistUploadInfo>

      {hasAnyUploadedFiles ? (
        <ChecklistFilesList>
          {checklistFiles.map(file => {
            const fileName =
              file.displayName ||
              file.display_name ||
              file.name ||
              file.versions?.[0]?.name
            const thumbnailUrl = file.thumbnailUrl || file.thumbnail_url

            return (
              <ChecklistReviewFileCard key={file.id}>
                <ChecklistReviewPreviewButton
                  type='button'
                  onClick={() => handlePreviewFile(file)}
                >
                  {thumbnailUrl ? (
                    <ChecklistReviewThumbnail
                      src={thumbnailUrl}
                      alt={fileName}
                    />
                  ) : (
                    <ChecklistReviewPlaceholder>
                      No preview
                    </ChecklistReviewPlaceholder>
                  )}
                </ChecklistReviewPreviewButton>

                <ChecklistReviewContent>
                  <ChecklistReviewHeader>
                    <ChecklistFileName>{fileName}</ChecklistFileName>
                    <ChecklistFileStatus>
                      Uploaded for this task
                    </ChecklistFileStatus>
                  </ChecklistReviewHeader>
                  <ChecklistReviewActions>
                    <ChecklistSecondaryButton
                      type='button'
                      onClick={() => handlePreviewFile(file)}
                    >
                      Preview
                    </ChecklistSecondaryButton>
                    <ChecklistSecondaryButton
                      type='button'
                      onClick={() => handleDownloadFile(file)}
                    >
                      Download
                    </ChecklistSecondaryButton>
                  </ChecklistReviewActions>
                </ChecklistReviewContent>
              </ChecklistReviewFileCard>
            )
          })}
        </ChecklistFilesList>
      ) : null}

      <ChecklistUploadActions>
        <ChecklistActionButton
          type='button'
          onClick={handleOpenUpload}
          disabled={!isSupported}
        >
          <UploadIcon />
          {hasUploadedFiles
            ? (taskFileTypeIds.length > 1 ? 'Re-upload files' : 'Re-upload file')
            : 'Open upload page'}
        </ChecklistActionButton>

        {!readOnly && (
          <ChecklistSecondaryButton
            type='button'
            onClick={triggerRefresh}
            disabled={!onTaskRefresh || refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh task data'}
          </ChecklistSecondaryButton>
        )}
      </ChecklistUploadActions>

      <ChecklistUploadMeta>
        {normalizedValue.lastOpenedAt && (
          <ChecklistMetaText>
            Upload page opened{' '}
            {new Date(normalizedValue.lastOpenedAt).toLocaleString()}
          </ChecklistMetaText>
        )}
        {normalizedValue.lastRefreshedAt && (
          <ChecklistMetaText>
            Last checked{' '}
            {new Date(normalizedValue.lastRefreshedAt).toLocaleString()}
          </ChecklistMetaText>
        )}
        {!hasUploadedFiles && normalizedValue.lastRefreshedAt && (
          <ChecklistUploadWarning>
            {isComplianceTask
              ? 'No evidence has been uploaded for this task yet.'
              : isTaskFileUpload
                ? 'One or more required document types are still missing.'
                : 'No uploaded file matched this checklist item yet.'}
          </ChecklistUploadWarning>
        )}
      </ChecklistUploadMeta>

      <ChecklistConfirmRow $readOnly={readOnly}>
        <StyledCheckbox
          type='checkbox'
          checked={normalizedValue.completed}
          disabled={readOnly || (isTaskFileUpload && !hasUploadedFiles)}
          onChange={e => handleCheckboxChange(e.target.checked)}
        />
        <ChecklistConfirmText>
          {isTaskFileUpload && !hasUploadedFiles
            ? 'Upload every required document type before marking this task ready for review.'
            : 'I finished the upload and it is ready for review.'}
        </ChecklistConfirmText>
      </ChecklistConfirmRow>

      {readOnly && normalizedValue.completedAt && (
        <ChecklistMetaText>
          Marked ready for review{' '}
          {new Date(normalizedValue.completedAt).toLocaleString()}
        </ChecklistMetaText>
      )}
    </ChecklistUploadCard>
  )
}

const FieldRenderer = ({
  field,
  value,
  commentValue,
  attachmentsValue,
  onChange,
  onCommentChange,
  onAttachmentsChange,
  readOnly = false,
  autoPopulated = false,
  errors = {},
  taskId,
  task,
  onTaskRefresh,
  entryKey,
  showMissing = false,
  missingNote = null
}) => {
  const fieldId = entryKey ? `${entryKey}_${field.key}` : field.key
  const fieldError = errors[field.key]
  const isRequired = field.required
  const isReadOnly = readOnly || field.readOnly

  const isMissing = showMissing && isRequired && !isReadOnly && (() => {
    if (value === null || value === undefined || value === '') return true
    if (field.type === 'file') return !(value && typeof value === 'object' && value.fileId)
    if (field.type === 'checkbox') return value !== true
    if (field.type === 'audit_question' || field.type === 'scored_question') return !(value && value.response)
    if (Array.isArray(value) && value.length === 0) return true
    return false
  })()

  // Soft tolerance check for number fields. Spec wants out-of-range to warn,
  // not block — the assignee may still record an anomaly and add a comment.
  const toleranceWarning = (() => {
    if (field.type !== 'number') return null
    if (value === '' || value === null || value === undefined) return null
    const numericValue = Number(value)
    if (Number.isNaN(numericValue)) return null
    const v = field.validation || {}
    const { min, max, tolerancePct } = v
    let lower = typeof min === 'number' ? min : null
    let upper = typeof max === 'number' ? max : null
    if (typeof tolerancePct === 'number' && tolerancePct > 0 && lower !== null && upper !== null) {
      const mid = (lower + upper) / 2
      const slack = (upper - lower) * (tolerancePct / 100)
      lower = lower - slack
      upper = upper + slack
      void mid
    }
    if (lower !== null && numericValue < lower) {
      return `Value ${numericValue} is below the expected range (min ${lower}).`
    }
    if (upper !== null && numericValue > upper) {
      return `Value ${numericValue} is above the expected range (max ${upper}).`
    }
    return null
  })()
  const [showComment, setShowComment] = useState(!!commentValue)
  const attachments = Array.isArray(attachmentsValue) ? attachmentsValue : []
  const [showAttachments, setShowAttachments] = useState(attachments.length > 0)

  const handleChange = newValue => {
    if (!isReadOnly && onChange) {
      onChange(field.key, newValue)
    }
  }

  const renderLabel = () => (
    <>
      <FieldLabel htmlFor={fieldId}>
        {field.label}
        {isRequired && <RequiredMark>*</RequiredMark>}
        {autoPopulated && (
          <AutoPopulatedBadge>Auto-filled from Building Passport</AutoPopulatedBadge>
        )}
      </FieldLabel>
      {field.description && <HelpText>{field.description}</HelpText>}
    </>
  )

  const renderField = () => {
    switch (field.type) {
      case 'audit_question':
      case 'scored_question':
        return (
          <AuditQuestionField
            field={field}
            value={value}
            onChange={handleChange}
            readOnly={isReadOnly}
            taskId={taskId}
            task={task}
            fieldId={fieldId}
            isRequired={isRequired}
          />
        )
      case 'statement':
        return <StatementText>{field.description}</StatementText>
      case FIELD_TYPES.text:
      case FIELD_TYPES.email:
      case FIELD_TYPES.phone:
        return (
          <StyledInput
            id={fieldId}
            type={field.type === FIELD_TYPES.phone ? 'tel' : field.type}
            value={value || ''}
            placeholder={field.placeholder || ''}
            onChange={e => handleChange(e.target.value)}
            readOnly={isReadOnly}
            $hasError={!!fieldError || isMissing}
            $readOnly={isReadOnly}
            maxLength={field.validation?.maxLength}
          />
        )

      case FIELD_TYPES.textarea:
        return (
          <StyledTextarea
            id={fieldId}
            value={value || ''}
            placeholder={field.placeholder || ''}
            onChange={e => handleChange(e.target.value)}
            readOnly={isReadOnly}
            $hasError={!!fieldError || isMissing}
            $readOnly={isReadOnly}
            maxLength={field.validation?.maxLength}
          />
        )

      case FIELD_TYPES.number:
        // Don't pass min/max to the browser — tolerance is a soft warning, not
        // a hard block. The ToleranceWarning below the field surfaces it.
        return (
          <StyledInput
            id={fieldId}
            type='number'
            value={value ?? ''}
            placeholder={field.placeholder || ''}
            onChange={e =>
              handleChange(e.target.value === '' ? '' : Number(e.target.value))
            }
            readOnly={isReadOnly}
            $hasError={!!fieldError || isMissing}
            $readOnly={isReadOnly}
          />
        )

      case FIELD_TYPES.date:
        return (
          <StyledInput
            id={fieldId}
            type='date'
            value={value || ''}
            onChange={e => handleChange(e.target.value)}
            readOnly={isReadOnly}
            $hasError={!!fieldError || isMissing}
            $readOnly={isReadOnly}
          />
        )

      case FIELD_TYPES.select:
        return (
          <SearchableSelect
            id={fieldId}
            value={value ?? null}
            onChange={v => handleChange(v)}
            options={(field.options || []).map(opt => ({ value: opt.value, label: opt.label }))}
            placeholder={field.placeholder || 'Select...'}
            disabled={isReadOnly}
            ariaLabel={field.label}
          />
        )

      case FIELD_TYPES.multiselect: {
        const selectedValues = Array.isArray(value) ? value : []
        return (
          <OptionsGroup>
            {(field.options || []).map(opt => (
              <OptionLabel key={opt.value}>
                <StyledCheckbox
                  type='checkbox'
                  checked={selectedValues.includes(opt.value)}
                  disabled={isReadOnly}
                  onChange={e => {
                    const newValues = e.target.checked
                      ? [...selectedValues, opt.value]
                      : selectedValues.filter(v => v !== opt.value)
                    handleChange(newValues)
                  }}
                />
                {opt.label}
              </OptionLabel>
            ))}
          </OptionsGroup>
        )
      }

      case FIELD_TYPES.checkbox:
        return (
          <OptionLabel>
            <StyledCheckbox
              type='checkbox'
              checked={!!value}
              disabled={isReadOnly}
              onChange={e => handleChange(e.target.checked)}
            />
            {field.placeholder || field.label}
          </OptionLabel>
        )

      case FIELD_TYPES.radio:
        return (
          <OptionsGroup>
            {(field.options || []).map(opt => (
              <OptionLabel key={opt.value}>
                <StyledRadio
                  type='radio'
                  name={fieldId}
                  value={opt.value}
                  checked={value === opt.value}
                  disabled={isReadOnly}
                  onChange={() => handleChange(opt.value)}
                />
                {opt.label}
              </OptionLabel>
            ))}
          </OptionsGroup>
        )

      case FIELD_TYPES.yesNo:
        return (
          <OptionsGroup>
            {[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map(opt => (
              <OptionLabel key={opt.value}>
                <StyledRadio
                  type='radio'
                  name={fieldId}
                  value={opt.value}
                  checked={value === opt.value}
                  disabled={isReadOnly}
                  onChange={() => handleChange(opt.value)}
                />
                {opt.label}
              </OptionLabel>
            ))}
          </OptionsGroup>
        )

      case FIELD_TYPES.file:
        return (
          <FileField
            field={field}
            value={value}
            onChange={handleChange}
            readOnly={isReadOnly}
            taskId={taskId}
          />
        )

      case FIELD_TYPES.image:
        // Image fields reuse the existing photo capture path — same upload
        // pipeline as `file` with `fileAcceptMode='photos_only'`, but
        // expressed at the schema level so reports can treat them
        // differently (gallery layout, thumbnails, etc.).
        return (
          <FileField
            field={{ ...field, fileAcceptMode: 'photos_only' }}
            value={value}
            onChange={handleChange}
            readOnly={isReadOnly}
            taskId={taskId}
          />
        )

      case FIELD_TYPES.video:
        return (
          <FileField
            field={{ ...field, fileAcceptMode: 'files_only', acceptedMimeTypes: ['video/*'] }}
            value={value}
            onChange={handleChange}
            readOnly={isReadOnly}
            taskId={taskId}
          />
        )

      case FIELD_TYPES.gps:
        return (
          <GpsField
            value={value}
            onChange={handleChange}
            readOnly={isReadOnly}
          />
        )

      case FIELD_TYPES.checklistUpload:
        return (
          <ChecklistUploadField
            value={value}
            onChange={handleChange}
            readOnly={isReadOnly}
            task={task}
            onTaskRefresh={onTaskRefresh}
          />
        )

      case FIELD_TYPES.signature:
        return (
          <SignatureField
            value={value}
            onChange={handleChange}
            readOnly={isReadOnly}
          />
        )

      default:
        return (
          <StyledInput
            id={fieldId}
            type='text'
            value={value || ''}
            onChange={e => handleChange(e.target.value)}
            readOnly={isReadOnly}
            $hasError={!!fieldError || isMissing}
            $readOnly={isReadOnly}
          />
        )
    }
  }

  return (
    <FieldWrapper data-field-id={fieldId}>
      {![FIELD_TYPES.checkbox, 'audit_question', 'scored_question', 'statement'].includes(field.type) && renderLabel()}
      {renderField()}
      {(field.allowAttachments || (isReadOnly && attachments.length > 0)) && (
        <AttachSection>
          {!showAttachments && !isReadOnly ? (
            <AttachBtn type='button' onClick={() => setShowAttachments(true)} aria-label={`Attach files to ${field.label}`}>
              <AttachFileRoundedIcon style={{ fontSize: 17 }} /> Attach files ({attachments.length})
            </AttachBtn>
          ) : (
            <>
              <AttachLabel>Attached files ({attachments.length})</AttachLabel>
              <FileList
                files={attachments}
                onChange={files => onAttachmentsChange && onAttachmentsChange(field.key, files)}
                readOnly={isReadOnly}
                taskId={taskId}
              />
            </>
          )}
        </AttachSection>
      )}
      {(field.allowComment || (isReadOnly && commentValue)) && (
        <CommentSection>
          {!showComment && !isReadOnly ? (
            <AddCommentBtn type='button' onClick={() => setShowComment(true)}>
              + Add comment
            </AddCommentBtn>
          ) : (showComment || commentValue) ? (
            <CommentTextarea
              value={commentValue || ''}
              onChange={e => onCommentChange && onCommentChange(field.key, e.target.value)}
              placeholder='Add a comment...'
              readOnly={isReadOnly}
              $readOnly={isReadOnly}
              rows={2}
            />
          ) : null}
        </CommentSection>
      )}
      {fieldError && <ErrorMessage>{fieldError}</ErrorMessage>}
      {isMissing && !fieldError && <MissingHint>{missingNote || 'This question needs an answer'}</MissingHint>}
      {toleranceWarning && !fieldError && <ToleranceWarning>{toleranceWarning}</ToleranceWarning>}
    </FieldWrapper>
  )
}

const SignatureField = ({ value, onChange, readOnly }) => {
  const sigRef = React.useRef(null)
  const wrapperRef = React.useRef(null)
  const [isEmpty, setIsEmpty] = React.useState(!value)

  // Sync canvas pixel dimensions with its CSS-rendered size so
  // signature_pad can track strokes correctly.
  const resizeCanvas = React.useCallback(() => {
    const pad = sigRef.current
    if (!pad) return
    const canvas = pad.getCanvas()
    const wrapper = wrapperRef.current
    if (!canvas || !wrapper) return

    const rect = wrapper.getBoundingClientRect()
    if (rect.width === 0) return

    canvas.width = rect.width
    canvas.height = 150
    pad.clear()
  }, [])

  React.useEffect(() => {
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)
    return () => window.removeEventListener('resize', resizeCanvas)
  }, [resizeCanvas])

  React.useEffect(() => {
    if (sigRef.current) {
      resizeCanvas()
      if (value) {
        try {
          sigRef.current.fromDataURL(value)
        } catch (e) {
          // ignore invalid data
        }
      }
    }
  }, [value, resizeCanvas])

  const handleEnd = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const dataUrl = sigRef.current.toDataURL('image/png')
      onChange(dataUrl)
      setIsEmpty(false)
    }
  }

  const handleClear = () => {
    if (sigRef.current) {
      sigRef.current.clear()
      onChange('')
      setIsEmpty(true)
    }
  }

  if (readOnly) {
    return value ? (
      <SignaturePreview>
        <img src={value} alt='Signature' />
      </SignaturePreview>
    ) : (
      <SignatureEmptyState>No signature provided</SignatureEmptyState>
    )
  }

  return (
    <SignatureWrapper>
      <SignatureCanvasWrapper ref={wrapperRef}>
        <SignatureCanvas
          ref={sigRef}
          canvasProps={{
            className: 'signature-canvas',
            style: { width: '100%', height: '150px' }
          }}
          onEnd={handleEnd}
        />
      </SignatureCanvasWrapper>
      <SignatureActions>
        {!isEmpty && (
          <SignatureClearBtn type='button' onClick={handleClear}>
            Clear
          </SignatureClearBtn>
        )}
        {isEmpty && <SignatureHint>Draw your signature above</SignatureHint>}
      </SignatureActions>
    </SignatureWrapper>
  )
}

const GpsField = ({ value, onChange, readOnly }) => {
  const [capturing, setCapturing] = useState(false)
  const [error, setError] = useState(null)

  // The form-response reducer recursively snake-cases nested response
  // values (a known gotcha — see snakeCaseKeys in ducks/reducer.js).
  // Read both casings so we work whether the value is fresh from the
  // browser geolocation API (camelCase) or reloaded from the API
  // (snake_case after the normaliser).
  const coords = (() => {
    if (!value) return null
    if (typeof value !== 'object' || value.lat == null || value.lng == null) return null
    return {
      lat: value.lat,
      lng: value.lng,
      accuracyM: value.accuracyM ?? value.accuracy_m ?? null,
      capturedAt: value.capturedAt ?? value.captured_at ?? null,
    }
  })()

  const capture = () => {
    if (readOnly) return
    setError(null)
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this device.')
      return
    }
    setCapturing(true)
    navigator.geolocation.getCurrentPosition(
      pos => {
        // Write both casings so this round-trips cleanly even before the
        // reducer normaliser runs on the next reload.
        const accuracy = pos.coords.accuracy ? Math.round(pos.coords.accuracy) : null
        const capturedAt = new Date().toISOString()
        onChange({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: accuracy,
          accuracy_m: accuracy,
          capturedAt,
          captured_at: capturedAt,
        })
        setCapturing(false)
      },
      err => {
        setError(err.message || 'Could not retrieve location.')
        setCapturing(false)
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    )
  }

  return (
    <GpsWrapper>
      {coords ? (
        <GpsDisplay>
          <div><strong>Lat:</strong> {Number(coords.lat).toFixed(6)}</div>
          <div><strong>Lng:</strong> {Number(coords.lng).toFixed(6)}</div>
          {coords.accuracyM != null && (
            <GpsMeta>Accuracy ±{coords.accuracyM} m</GpsMeta>
          )}
          {coords.capturedAt && (
            <GpsMeta>Captured {new Date(coords.capturedAt).toLocaleString()}</GpsMeta>
          )}
        </GpsDisplay>
      ) : (
        <GpsEmpty>No location captured yet.</GpsEmpty>
      )}
      {!readOnly && (
        <GpsButton type='button' onClick={capture} disabled={capturing}>
          {capturing ? 'Locating…' : (coords ? 'Recapture location' : 'Capture location')}
        </GpsButton>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </GpsWrapper>
  )
}

export default FieldRenderer
export { FileField }

const StatementText = styled.p`
  margin: 0;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f8f9fb;
  border: 1px solid #eaecf0;
  font-size: 15px;
  line-height: 1.6;
  color: #344054;
  white-space: pre-line;
`

const AttachSection = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const AttachLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #344054;
`

const AttachBtn = styled.button`
  font: inherit;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 6px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  color: #344054;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: #33becc; color: #1f7f89; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

// --- Styled Components ---

const FieldWrapper = styled.div`
  margin-bottom: 16px;
`

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.error || theme.colors.primary};
  margin-left: 2px;
`

const AutoPopulatedBadge = styled.span`
  display: inline-flex;
  margin-left: 8px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 500;
  color: #0e7490;
  background: #e0f2fe;
  border-radius: 10px;
  vertical-align: middle;
`

const HelpText = styled.p`
  margin: 2px 0 6px 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#666'};
  font-style: italic;
`

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid
    ${({ $hasError, theme }) =>
      $hasError
        ? theme.colors.error || theme.colors.primary
        : theme.colors.mediumGrey};
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  background-color: ${({ $readOnly, theme }) =>
    $readOnly ? theme.colors.lightGrey : '#fff'};
  color: ${({ theme }) => theme.colors.haiti};
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ $hasError, theme }) =>
      $hasError
        ? theme.colors.error || theme.colors.primary
        : theme.colors.secondary};
    box-shadow: 0 0 0 3px
      ${({ $hasError, theme }) =>
        $hasError ? 'rgba(209, 73, 37, 0.1)' : 'rgba(51, 190, 204, 0.1)'};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid
    ${({ $hasError, theme }) =>
      $hasError
        ? theme.colors.error || theme.colors.primary
        : theme.colors.mediumGrey};
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  min-height: 100px;
  resize: vertical;
  background-color: ${({ $readOnly, theme }) =>
    $readOnly ? theme.colors.lightGrey : '#fff'};
  color: ${({ theme }) => theme.colors.haiti};
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ $hasError, theme }) =>
      $hasError
        ? theme.colors.error || theme.colors.primary
        : theme.colors.secondary};
    box-shadow: 0 0 0 3px
      ${({ $hasError, theme }) =>
        $hasError ? 'rgba(209, 73, 37, 0.1)' : 'rgba(51, 190, 204, 0.1)'};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const OptionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
`

const StyledCheckbox = styled.input`
  accent-color: ${({ theme }) => theme.colors.primary};
`

const StyledRadio = styled.input`
  accent-color: ${({ theme }) => theme.colors.primary};
`

const SignatureWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  overflow: hidden;
`

const SignatureCanvasWrapper = styled.div`
  background: #fafafa;
  border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGrey};

  .signature-canvas {
    display: block;
    cursor: crosshair;
  }
`

const SignatureActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  min-height: 36px;
`

const SignatureClearBtn = styled.button`
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #dc2626;
  background: transparent;
  border: 1px solid #fecaca;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #fef2f2;
  }
`

const SignatureHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
`

const SignaturePreview = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;

  img {
    max-width: 100%;
    max-height: 150px;
    display: block;
  }
`

const SignatureEmptyState = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  font-size: 13px;
`

const FieldHelpText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
  margin-bottom: 6px;
  line-height: 1.4;
`

const MissingHint = styled.div`
  color: #b45309;
  font-size: 14px;
  font-weight: 600;
  margin-top: 4px;
`

const ToleranceWarning = styled.div`
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error || theme.colors.primary};
  font-size: 12px;
  margin-top: 4px;
`

// --- GPS Field Styled Components ---

const GpsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const GpsDisplay = styled.div`
  background: ${({ theme }) => theme.colors.backgroundColor};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  font-family: 'SFMono-Regular', Menlo, monospace;
`

const GpsEmpty = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  font-style: italic;
`

const GpsMeta = styled.div`
  font-family: ${({ theme }) => theme.fonts?.primary || 'inherit'};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-top: 4px;
`

const GpsButton = styled.button`
  align-self: flex-start;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

// --- File Field Styled Components ---

const FileDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $hasImage }) => ($hasImage ? '8px' : '10px 14px')};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.lightGrey};
  margin-bottom: 8px;
  gap: 12px;
`

const FileThumbnail = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 6px;
  object-fit: cover;
  cursor: pointer;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

const FileSize = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
`

const FileName = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const FileLink = styled.a`
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const FileNameButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  overflow-wrap: anywhere;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`

const PhotoDescription = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.haiti};
`

const RemoveButton = styled.button`
  padding: 4px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error || theme.colors.primary};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.error || theme.colors.primary};
  border-radius: 20px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.linen || '#fbede9'};
  }
`

const FileActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`

const ReplaceButton = styled.button`
  padding: 4px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary || '#33becc'};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.secondary || '#33becc'};
  border-radius: 20px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary || '#33becc'};
    color: white;
  }
`

const PendingBadge = styled.span`
  position: absolute;
  bottom: 4px;
  left: 4px;
  background: #c84a31;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
`

const GeneratingText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  font-style: italic;
  margin-bottom: 8px;
`

const DescriptionLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-bottom: 4px;
`

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  min-height: 60px;
  resize: vertical;
  outline: none;
  background-color: ${({ $readOnly, theme }) =>
    $readOnly ? theme.colors.lightGrey : '#fff'};
  color: ${({ theme }) => theme.colors.haiti};
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const UploadProgressWrapper = styled.div`
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.lightGrey};
`

const UploadProgressText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const ProgressBarTrack = styled.div`
  height: 6px;
  background-color: ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 3px;
  overflow: hidden;
`

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.secondary};
  border-radius: 3px;
  transition: width 0.2s ease;
`

const FileUploadOptions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: ${({ $primary, theme }) =>
    $primary ? 'none' : `1px solid ${theme.colors.mediumGrey}`};
  background: ${({ $primary, theme }) =>
    $primary ? theme.colors.primary : 'white'};
  color: ${({ $primary, theme }) => ($primary ? 'white' : theme.colors.haiti)};

  &:hover {
    background: ${({ $primary, theme }) =>
      $primary
        ? theme.colors.primaryDark || '#b73d1f'
        : theme.colors.lightGrey};
    box-shadow: ${({ $primary }) =>
      $primary ? '0 4px 12px rgba(210, 71, 36, 0.3)' : 'none'};
  }

  &:active {
    transform: scale(0.98);
  }
`

const CameraIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z' />
    <circle cx='12' cy='13' r='4' />
  </svg>
)

const UploadIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
    <polyline points='17,8 12,3 7,8' />
    <line x1='12' y1='3' x2='12' y2='15' />
  </svg>
)

const ChecklistUploadCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 10px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.lightGrey};
`

const ChecklistUploadInfo = styled.div`
  margin-bottom: 12px;
`

const ChecklistUploadTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const ChecklistUploadText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
`

const ChecklistUploadWarning = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error || theme.colors.primary};
`

const RequiredFileTypes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`

const RequiredFileType = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border-radius: 999px;
  background: #ecfeff;
  border: 1px solid #a5f3fc;
  color: #0e7490;
  font-size: 12px;
  font-weight: 600;
`

const ChecklistUploadActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`

const ChecklistActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const ChecklistSecondaryButton = styled.button`
  padding: 10px 16px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const ChecklistUploadMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`

const ChecklistMetaText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
`

const ChecklistConfirmRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
`

const ChecklistFilesList = styled.div`
  display: grid;
  gap: 10px;
`

const ChecklistReviewFileCard = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 14px;
  align-items: stretch;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  background: #fff;
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ChecklistReviewPreviewButton = styled.button`
  border: 0;
  padding: 0;
  background: ${({ theme }) => theme.colors.lightGrey};
  cursor: pointer;
  min-height: 150px;
`

const ChecklistReviewThumbnail = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  min-height: 150px;
  object-fit: cover;
`

const ChecklistReviewPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 150px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#666'};
  font-size: 13px;
`

const ChecklistReviewContent = styled.div`
  padding: 16px 16px 16px 0;

  @media (max-width: 768px) {
    padding: 0 16px 16px;
  }
`

const ChecklistReviewActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
`

const ChecklistReviewHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ChecklistFileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  overflow-wrap: anywhere;
  word-break: break-word;
`

const ChecklistFileStatus = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 500;
`

const ChecklistConfirmText = styled.span`
  line-height: 1.45;
`

const CommentSection = styled.div`
  margin-top: 8px;
`

const AddCommentBtn = styled.button`
  padding: 4px 0;
  font-size: 12px;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #334155;
  }
`

const CommentTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  resize: vertical;
  min-height: 48px;
  font-family: inherit;
  color: #334155;
  background: ${({ $readOnly }) => $readOnly ? '#f8fafc' : '#fff'};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #94a3b8;
  }
`
